import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from 'moment-timezone';
import { AlertTriangle, ExternalLink, History, Loader2, Plus, RotateCcw, Smartphone, Sparkles, Trash2, X } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { SOURCES, SOURCE_LABEL } from '../../../lib/messageCore';
import { useCreateMessage, useDeleteMessage, useMessageHistory, useRestoreMessage, useSaveMessage } from '../../../services/messages';
import Select from './Select';
import {
  CARD,
  CHANNEL,
  OWN_KEY_RE,
  SOURCE_SHORT,
  checkMessage,
  fieldsOf,
  fillInsAllowed,
  kindOf,
  readWording,
  sameFields,
  usedLine,
  wordingKeys,
  wordingLabel,
} from './messageUi';

// One message (Messages, Nic 2026-10-22): the same words for everyone, or
// words for each place a person can come from (a partner, an event, our
// website, a referral, a call), plus a partner's or an event's own. The
// preview shows each wording as a person from there reads it.

const INPUT =
  'block w-full rounded-xl bg-white px-[12px] py-[8px] text-[14px] text-slate-800 outline-none ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-400 dark:!bg-white/5 dark:text-slate-100 dark:ring-white/10 dark:focus:ring-sky-400/50';
const SMALL_BTN =
  'inline-flex h-8 items-center gap-[6px] whitespace-nowrap rounded-lg px-[10px] text-xs font-semibold text-slate-600 outline-none ring-1 ring-slate-200 hover:bg-slate-50 hover:text-slate-800 focus:outline-none disabled:opacity-40 dark:text-slate-300 dark:ring-white/10 dark:hover:!bg-white/5';

// One wording tab.
const Tab = ({ on, bad, label, onClick, onRemove }) => (
  <span
    className={cn(
      'inline-flex items-center rounded-full text-[12px] font-semibold ring-1',
      on ? 'bg-slate-900 text-white ring-slate-900 dark:!bg-slate-100 dark:!text-slate-900 dark:ring-slate-100' : 'text-slate-600 ring-slate-200 dark:text-slate-300 dark:ring-white/10',
    )}
  >
    <button type="button" onClick={onClick} className="inline-flex items-center gap-[6px] rounded-full py-[4px] pl-[10px] pr-[10px] outline-none focus:outline-none">
      {bad ? <span className="h-[6px] w-[6px] rounded-full bg-rose-500" title="Needs a fix" /> : null}
      {label}
    </button>
    {onRemove ? (
      <button type="button" onClick={onRemove} aria-label={`Remove ${label}'s own wording`} className="-ml-[4px] mr-[4px] rounded-full p-[2px] opacity-70 outline-none hover:opacity-100 focus:outline-none">
        <X size={11} />
      </button>
    ) : null}
  </span>
);

// How it reads, for the wording on screen.
// Sales texts go to leads and end with the STOP line; Onboarding texts go to
// signed clients and never do (Nic 2026-10-25).
const Preview = ({ channel, read, team }) => (
  <div className={cn(CARD, 'overflow-hidden')}>
    <div className="flex items-center gap-[8px] border-b border-slate-100 px-[16px] py-[10px] dark:border-white/10">
      <span className="flex h-6 w-6 items-center justify-center rounded-md bg-emerald-500 text-white">
        <Smartphone size={12} />
      </span>
      <span className="text-[12px] font-bold uppercase tracking-wide text-emerald-900 dark:text-emerald-100">How it reads</span>
    </div>
    <div className="p-[14px]">
      <div className="mb-[8px] text-[11px] font-semibold text-slate-400">As {read.who} reads it</div>
      {channel === 'text' ? (
        <div className="ml-[18px] whitespace-pre-wrap break-words rounded-2xl rounded-br-md bg-sky-500 px-[12px] py-[9px] text-[13px] leading-relaxed text-white [overflow-wrap:anywhere]">
          {read.body || <span className="opacity-70">Nothing to send yet.</span>}
        </div>
      ) : (
        <div className="rounded-xl bg-slate-50 px-[12px] py-[10px] text-[13px] leading-relaxed text-slate-800 ring-1 ring-slate-200 dark:!bg-white/5 dark:text-slate-100 dark:ring-white/10">
          {channel === 'email' ? (
            <div className="mb-[8px] border-b border-slate-200 pb-[6px] font-semibold dark:border-white/10">{read.subject || <span className="text-slate-400">No subject</span>}</div>
          ) : null}
          <div className="whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{read.body || <span className="text-slate-400">Nothing yet.</span>}</div>
        </div>
      )}
      <div className="mt-[6px] flex flex-wrap justify-end gap-x-[10px] text-[10px] text-slate-400">
        <span>{read.length} characters</span>
        {channel === 'text' ? <span>{read.segments} text{read.segments === 1 ? '' : 's'} long</span> : null}
        {channel === 'text' && team !== 'onboarding' ? <span>a reply-STOP line is added</span> : null}
      </div>
      {read.stuck.length ? (
        <div className="mt-[8px] flex items-start gap-[6px] rounded-lg bg-amber-50 px-[8px] py-[5px] text-[11px] font-semibold text-amber-800 dark:!bg-amber-500/10 dark:text-amber-200">
          <AlertTriangle size={12} className="mt-[2px] shrink-0" /> Won't send for someone with no {read.stuck.includes('bio') ? 'bio' : 'phone number'} on file.
        </div>
      ) : null}
    </div>
  </div>
);

const HistoryList = ({ messageId, onRestored }) => {
  const q = useMessageHistory(messageId, true);
  const restore = useRestoreMessage();
  const history = q.data?.history || [];
  if (q.isLoading) return <Loader2 size={16} className="m-[14px] animate-spin text-slate-400" />;
  return (
    <div className="divide-y divide-slate-100 dark:divide-white/10">
      {history.map((h, i) => (
        <div key={h.historyId} className="flex items-center gap-[10px] px-[16px] py-[8px]">
          <div className="min-w-0 flex-1">
            <div className="text-[12px] font-semibold text-slate-700 dark:text-slate-200">
              {moment(h.savedAt).tz('America/Chicago').format('MMM D, YYYY h:mm A')}
              {h.savedByName ? ` · ${h.savedByName}` : ''}
            </div>
            <div className="truncate text-[11px] text-slate-400">{h.note || (i === 0 ? 'Current' : 'Saved')}</div>
          </div>
          {i === 0 ? (
            <span className="text-[11px] font-semibold text-slate-400">Current</span>
          ) : (
            <button
              type="button"
              disabled={restore.isLoading}
              onClick={async () => {
                // eslint-disable-next-line no-alert
                if (!window.confirm('Put these words back? The words now stay in History.')) return;
                try {
                  const res = await restore.mutateAsync({ messageId, historyId: h.historyId });
                  toast.success('Put back. It sends this way from now on.');
                  onRestored(res.message);
                } catch (e) {
                  toast.error(e?.response?.data?.msg || "That couldn't be put back.");
                }
              }}
              className={SMALL_BTN}
            >
              <RotateCcw size={12} /> Restore
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

// The bottom bar's height; the page keeps this much room below its content.
export const SAVE_BAR_HEIGHT = 48;

const MessageEditor = ({ message, data, onSaved, onDeleted, onDirtyChange }) => {
  const isNew = !message.messageId;
  const [work, setWork] = useState(() => ({ ...message, ...fieldsOf(message) }));
  const [tab, setTab] = useState(() => wordingKeys(message)[0]);
  const [showHistory, setShowHistory] = useState(false);
  const [serverErrors, setServerErrors] = useState([]);
  const boxRef = useRef(null);
  const caretRef = useRef(null);
  const create = useCreateMessage();
  const save = useSaveMessage();
  const remove = useDeleteMessage();
  const partners = data.partners;

  useEffect(() => {
    caretRef.current = null;
  }, [tab]);
  const dirty = isNew ? !!(work.name.trim() || Object.values(work.wordings).some((w) => w?.body?.trim())) : !sameFields(work, message);
  useEffect(() => onDirtyChange?.(dirty), [dirty, onDirtyChange]);

  const keys = wordingKeys(work);
  const key = keys.includes(tab) ? tab : keys[0];
  const wording = useMemo(() => work.wordings[key] || { body: '' }, [work.wordings, key]);
  const { byKey, errors, warnings } = useMemo(() => checkMessage(work, { limits: data.limits, partners }), [work, data.limits, partners]);
  const read = useMemo(() => readWording(wording, key, partners, work.channel), [wording, key, partners, work.channel]);
  const fillIns = fillInsAllowed(work, key);
  const max = data.limits?.[work.channel] || 1000;

  const setWording = (patch) => setWork((w) => ({ ...w, wordings: { ...w.wordings, [key]: { ...(w.wordings[key] || { body: '' }), ...patch } } }));
  const setMode = (mode) => {
    if (mode === work.mode) return;
    setWork((w) => {
      if (mode === 'by_source') {
        // Every source starts from the words for everyone.
        const all = w.wordings.all || { body: '' };
        return { ...w, mode, wordings: Object.fromEntries(SOURCES.map((k) => [k, { ...all }])) };
      }
      return { ...w, mode, wordings: { all: { ...(w.wordings[key] || w.wordings.partner || { body: '' }) } } };
    });
    setTab(mode === 'one' ? 'all' : 'partner');
  };
  const insert = (label) => {
    const text = wording.body || '';
    const token = `{${label}}`;
    const caret = caretRef.current;
    const start = caret ? caret.start : text.length;
    const end = caret ? caret.end : text.length;
    const glue = !caret && text && !/\s$/.test(text) ? ' ' : '';
    setWording({ body: `${text.slice(0, start)}${glue}${token}${text.slice(end)}` });
    const at = start + glue.length + token.length;
    caretRef.current = { start: at, end: at };
    requestAnimationFrame(() => {
      const el = boxRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(at, at);
    });
  };

  // A partner's or an event's own wording, from a copy of its source's.
  const ownTaken = new Set(Object.keys(work.wordings).filter((k) => OWN_KEY_RE.test(k)));
  const addOwn = (kind, partnerId) => {
    const k = `${kind}:${partnerId}`;
    setWork((w) => ({ ...w, wordings: { ...w.wordings, [k]: { ...(w.wordings[kind] || { body: '' }) } } }));
    setTab(k);
  };
  const removeOwn = (k) => {
    setWork((w) => {
      const next = { ...w.wordings };
      delete next[k];
      return { ...w, wordings: next };
    });
    setTab(kindOf(k));
  };
  const copyFrom = (from) => setWording({ ...work.wordings[from] });

  const doSave = async () => {
    setServerErrors([]);
    try {
      const body = fieldsOf(work);
      const res = isNew ? await create.mutateAsync(body) : await save.mutateAsync({ messageId: message.messageId, ...body });
      (res.warnings || []).forEach((w) => toast.info(w));
      toast.success(isNew ? 'Message made. Pick it on a cadence step.' : 'Saved. It sends this way from now on.');
      if (res.message) setWork((w) => ({ ...w, ...fieldsOf(res.message) }));
      onSaved(res.message);
    } catch (e) {
      const b = e?.response?.data;
      setServerErrors(b?.errors || []);
      toast.error(b?.msg || "That message couldn't be saved.");
    }
  };
  const doDelete = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete "${message.name}"? This can't be undone.`)) return;
    try {
      await remove.mutateAsync({ messageId: message.messageId });
      toast.success('Deleted.');
      onDeleted();
    } catch (e) {
      toast.error(e?.response?.data?.msg || "That message couldn't be deleted.");
    }
  };
  const saving = create.isLoading || save.isLoading;
  const shown = [...errors, ...serverErrors.filter((e) => !errors.includes(e))];
  const canDelete = !isNew && work.kind === 'cadence' && !message.uses?.length;

  return (
    <div className="grid min-h-0 gap-[12px] xl:grid-cols-[minmax(0,1fr)_22rem]">
      <div className="flex min-w-0 flex-col gap-[12px]">
        {/* Name, kind, where it's used, and the one-or-by-source switch */}
        <div className={cn(CARD, 'p-[14px]')}>
          <div className="flex flex-wrap items-center gap-[10px]">
            <span className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-white', CHANNEL[work.channel]?.tint)}>
              {React.createElement(CHANNEL[work.channel]?.Icon || CHANNEL.text.Icon, { size: 15 })}
            </span>
            <input
              value={work.name}
              onChange={(e) => setWork((w) => ({ ...w, name: e.target.value }))}
              maxLength={120}
              placeholder="Name it, like Day 4 check in"
              aria-label="Name"
              className="min-w-[180px] flex-1 rounded-lg bg-transparent px-[6px] py-[4px] text-[16px] font-bold text-slate-900 outline-none hover:bg-slate-50 focus:bg-slate-50 focus:outline-none dark:text-white dark:hover:!bg-white/5 dark:focus:!bg-white/5"
            />
            {dirty && !isNew ? <span className="rounded-full bg-amber-500/10 px-[8px] py-[2px] text-[11px] font-semibold text-amber-700 dark:text-amber-300">Not saved</span> : null}
          </div>
          <div className="mt-[6px] flex flex-wrap items-center gap-x-[8px] gap-y-[4px] px-[6px] text-[12px] text-slate-500 dark:text-slate-400">
            {isNew ? (
              <span className="w-[150px]">
                <Select
                  size="sm"
                  ariaLabel="Kind"
                  value={work.channel}
                  options={Object.entries(CHANNEL).map(([k, c]) => ({ value: k, label: c.label }))}
                  onChange={(v) => setWork((w) => ({ ...w, channel: v }))}
                />
              </span>
            ) : (
              <span className="font-semibold text-slate-600 dark:text-slate-300">{CHANNEL[work.channel]?.label}</span>
            )}
            <span aria-hidden>·</span>
            <span>{isNew ? 'Pick it on a cadence step once it is made' : usedLine(message)}</span>
            {work.kind === 'cadence' && !isNew ? (
              <Link to="/admin/cadence-editor" className="inline-flex items-center gap-[3px] font-semibold text-sky-600 no-underline hover:underline dark:text-sky-400">
                Cadence Editor <ExternalLink size={11} />
              </Link>
            ) : null}
            {message.switchOn !== null && message.switchOn !== undefined ? (
              <span className={cn('rounded-full px-[7px] py-[1px] text-[11px] font-semibold', message.switchOn ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300' : 'bg-slate-900/[0.05] text-slate-500 dark:!bg-white/10')}>
                {message.switchOn ? 'On' : 'Off'} (Settings menu)
              </span>
            ) : null}
          </div>
          <div className="mt-[12px] inline-flex rounded-xl bg-slate-100 p-[3px] dark:!bg-white/10" role="radiogroup" aria-label="Who gets which words">
            {[
              { k: 'one', label: 'Same for everyone' },
              { k: 'by_source', label: 'By where they came from' },
            ].map(({ k, label }) => (
              <button
                key={k}
                type="button"
                role="radio"
                aria-checked={work.mode === k}
                onClick={() => setMode(k)}
                className={cn(
                  'rounded-lg px-[12px] py-[5px] text-[12px] font-semibold outline-none focus:outline-none',
                  work.mode === k ? 'bg-white text-slate-900 shadow-sm dark:!bg-surface-elevated dark:text-white' : 'text-slate-500 hover:text-slate-700 dark:text-slate-400',
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* The words */}
        <div className={cn(CARD, 'overflow-hidden')}>
          {work.mode === 'by_source' ? (
            <div className="flex flex-wrap items-center gap-[6px] border-b border-slate-100 px-[14px] py-[10px] dark:border-white/10">
              {keys.map((k) => (
                <Tab
                  key={k}
                  on={k === key}
                  bad={!!byKey[k]}
                  label={wordingLabel(k, partners)}
                  onClick={() => setTab(k)}
                  onRemove={OWN_KEY_RE.test(k) ? () => removeOwn(k) : null}
                />
              ))}
              <span className="ml-auto flex gap-[6px]">
                {['partner', 'event'].map((kind) => {
                  const free = partners.filter((p) => p.kind === kind && p.active && !ownTaken.has(`${kind}:${p.partnerId}`));
                  return (
                    <span key={kind} className="w-[150px]">
                      <Select
                        size="sm"
                        ariaLabel={kind === 'partner' ? 'Words for one partner' : 'Words for one event'}
                        value=""
                        placeholder={kind === 'partner' ? '+ A partner' : '+ An event'}
                        disabled={!free.length}
                        options={free.map((p) => ({ value: p.partnerId, label: p.partnerName }))}
                        onChange={(id) => addOwn(kind, id)}
                      />
                    </span>
                  );
                })}
              </span>
            </div>
          ) : null}
          <div className="p-[14px]">
            <div className="mb-[8px] flex flex-wrap items-center gap-[8px]">
              <span className="text-[12px] font-semibold text-slate-500 dark:text-slate-400">
                {work.mode === 'one'
                  ? 'Everyone gets these words.'
                  : OWN_KEY_RE.test(key)
                    ? `Only ${wordingLabel(key, partners)}'s ${kindOf(key) === 'event' ? 'sign-ups' : 'leads'} get these words.`
                    : `${SOURCE_LABEL[key]}: everyone from there gets these words${['partner', 'event'].includes(key) ? ` (unless their ${key} has its own)` : ''}.`}
              </span>
              {work.mode === 'by_source' ? (
                <span className="ml-auto w-[170px]">
                  <Select
                    size="sm"
                    ariaLabel="Copy the words from"
                    value=""
                    placeholder="Copy from..."
                    options={keys.filter((k) => k !== key).map((k) => ({ value: k, label: wordingLabel(k, partners) }))}
                    onChange={copyFrom}
                  />
                </span>
              ) : null}
            </div>
            {work.channel === 'email' ? (
              <input
                value={wording.subject || ''}
                onChange={(e) => setWording({ subject: e.target.value })}
                maxLength={data.limits?.subject || 200}
                placeholder="Subject"
                aria-label="Subject"
                className={cn(INPUT, 'mb-[8px] font-semibold')}
              />
            ) : null}
            <textarea
              ref={boxRef}
              value={wording.body || ''}
              rows={work.channel === 'text' ? 6 : 11}
              onChange={(e) => {
                setWording({ body: e.target.value });
                caretRef.current = { start: e.target.selectionStart, end: e.target.selectionEnd };
              }}
              onSelect={(e) => {
                caretRef.current = { start: e.target.selectionStart, end: e.target.selectionEnd };
              }}
              aria-label="Words"
              className={cn(INPUT, 'resize-y px-[14px] py-[12px] leading-relaxed')}
            />
            <div className="mt-[4px] text-right text-[11px] tabular-nums text-slate-400">
              {(wording.body || '').length}/{max}
            </div>
            <div className="mt-[8px] flex flex-wrap items-center gap-[6px]">
              <Sparkles size={13} className="text-violet-500" />
              {fillIns.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  title={f.hint}
                  onClick={() => insert(f.label)}
                  className={cn(
                    'rounded-lg px-[8px] py-[3px] text-[12px] font-bold outline-none ring-1 focus:outline-none',
                    f.source || (work.required || []).includes(f.key)
                      ? 'bg-violet-600 text-white ring-violet-600 hover:bg-violet-500'
                      : 'bg-violet-50 text-violet-800 ring-violet-200 hover:bg-violet-100 dark:!bg-violet-500/10 dark:text-violet-100 dark:ring-violet-400/30',
                  )}
                >
                  {`{${f.label}}`}
                </button>
              ))}
            </div>
            <div className="mt-[10px] space-y-[4px] text-[11px]">
              {(byKey[key] || []).map((e) => (
                <div key={e} className="flex items-start gap-[6px] rounded-lg bg-rose-50 px-[8px] py-[4px] font-semibold text-rose-700 dark:!bg-rose-500/10 dark:text-rose-300">
                  <AlertTriangle size={12} className="mt-[2px] shrink-0" /> {e}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* History */}
        {!isNew ? (
          <div className={cn(CARD, 'overflow-hidden')}>
            <button type="button" onClick={() => setShowHistory((v) => !v)} className="flex w-full items-center gap-[8px] px-[16px] py-[10px] text-left outline-none focus:outline-none">
              <History size={14} className="text-slate-400" />
              <span className="text-[12px] font-bold uppercase tracking-wide text-slate-700 dark:text-slate-200">History</span>
              <span className="ml-auto text-[11px] text-slate-400">{showHistory ? 'Hide' : 'Every save, and put one back'}</span>
            </button>
            {showHistory ? (
              <div className="border-t border-slate-100 dark:border-white/10">
                <HistoryList
                  messageId={message.messageId}
                  onRestored={(m) => {
                    if (m) setWork((w) => ({ ...w, ...fieldsOf(m) }));
                    setShowHistory(false);
                  }}
                />
              </div>
            ) : null}
          </div>
        ) : null}

        {/* Save bar: a thin bar across the whole bottom of the window (Nic
            2026-10-22: a floating card covered what you were reading). The
            page leaves SAVE_BAR_HEIGHT of room for it. */}
        <div className="fixed inset-x-0 bottom-0 z-[30] border-t border-slate-200 bg-white/95 backdrop-blur dark:border-white/10 dark:!bg-surface-elevated/95">
          <div className="mx-auto flex h-[48px] w-full max-w-screen-2xl items-center gap-[10px] px-[16px]">
          <span className="mr-auto min-w-0 truncate text-xs text-slate-500 dark:text-slate-400">
            {shown.length
              ? `${shown.length} thing${shown.length === 1 ? '' : 's'} to fix before saving.`
              : warnings.length
                ? warnings[0]
                : dirty
                  ? 'Not saved yet.'
                  : 'Everything is saved.'}
          </span>
          {canDelete ? (
            <button type="button" onClick={doDelete} disabled={remove.isLoading} className={cn(SMALL_BTN, 'text-rose-600 dark:text-rose-300')}>
              <Trash2 size={13} /> Delete
            </button>
          ) : null}
          {!isNew && dirty ? (
            <button type="button" onClick={() => setWork((w) => ({ ...w, ...fieldsOf(message) }))} className={SMALL_BTN}>
              Undo changes
            </button>
          ) : null}
          <button
            type="button"
            disabled={!dirty || errors.length > 0 || saving}
            onClick={doSave}
            className="inline-flex h-8 items-center gap-[6px] whitespace-nowrap rounded-lg bg-slate-900 px-[18px] text-sm font-semibold text-white outline-none hover:bg-slate-700 focus:outline-none disabled:opacity-40 dark:!bg-slate-100 dark:!text-slate-900"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : isNew ? <Plus size={14} /> : null}
            {isNew ? 'Make message' : 'Save'}
          </button>
          </div>
        </div>
      </div>

      <div className="min-w-0 xl:sticky xl:top-[12px] xl:self-start">
        <Preview channel={work.channel} read={read} team={work.team} />
        {work.mode === 'by_source' ? (
          <div className="mt-[10px] px-[4px] text-[11px] leading-relaxed text-slate-400">
            {SOURCES.map((k) => SOURCE_SHORT[k]).join(', ')}: every lead is exactly one of these, so every lead gets words that fit.
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default MessageEditor;
